import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Box, Alert, CircularProgress, Stepper, Step, StepLabel, Paper, Divider } from '@mui/material';
import { Icon } from '@iconify/react';
import { Grid } from '@mui/material';
interface AmendTransactionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string, newAccount: string, fetchedTitle: string) => Promise<void>;
  transaction: any; // Using any here to avoid importing complex types, but it expects the full transaction object
}
const AmendTransactionDialog: React.FC<AmendTransactionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  transaction
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [newAccount, setNewAccount] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchedTitle, setFetchedTitle] = useState<string | null>(null);
  const [isTitleAccepted, setIsTitleAccepted] = useState<boolean | null>(null);
  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setActiveStep(0);
      setNewAccount('');
      setReason('');
      setError(null);
      setFetchedTitle(null);
      setIsTitleAccepted(null);
    }
  }, [open]);
  if (!transaction) return null;
  const isEligible = (transaction.payoutType === 'FT' || transaction.payoutType === 'IBFT') && transaction.currentState === 'TITLE_FETCH' && transaction.lifecycleStatus === 'FAILED';
  const handleFetchTitle = async () => {
    if (!newAccount.trim()) {
      setError('Please enter a new account number');
      return;
    }
    setIsLoading(true);
    setError(null);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      // Mock fetched title based on input to make it feel dynamic
      const mockTitle = `MUHAMMAD ${transaction.beneficiaryName.split(' ')[0] || 'ALI'} KHAN`.toUpperCase();
      setFetchedTitle(mockTitle);
      setActiveStep(1);
    }, 1000);
  };
  const handleAcceptTitle = () => {
    setIsTitleAccepted(true);
    setActiveStep(2);
  };
  const handleRejectTitle = () => {
    setIsTitleAccepted(false);
    setFetchedTitle(null);
    setActiveStep(0);
  };
  const handleConfirm = async () => {
    if (!reason.trim()) {
      setError('Amendment reason is required');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await onConfirm(reason, newAccount, fetchedTitle!);
      onClose();
    } catch (err) {
      setError('Failed to submit amendment request');
    } finally {
      setIsLoading(false);
    }
  };
  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };
  const steps = ['Enter Account', 'Verify Title', 'Submit Reason'];
  return <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth data-id="element-68">
      <DialogTitle data-id="element-69">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }} data-id="element-70">
          <Icon icon="lucide:edit" width={24} color="#0EA5E9" data-id="element-71" />
          <Typography variant="h6" fontWeight={600} data-id="element-72">
            Amend Transaction
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent data-id="element-73">
        <Box sx={{
        mt: 1,
        mb: 3
      }} data-id="element-74">
          <Typography variant="body2" color="text.secondary" sx={{
          mb: 0.5
        }} data-id="element-75">
            Transaction ID: <strong data-id="element-76">{transaction.id}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{
          mb: 2
        }} data-id="element-77">
            Current Beneficiary: <strong data-id="element-78">{transaction.beneficiaryName}</strong>
          </Typography>

          {!isEligible ? <Alert severity="error" sx={{
          mb: 2
        }} data-id="element-79">
              <Typography variant="body2" fontWeight={600} data-id="element-80">
                Transaction not eligible for amendment.
              </Typography>
              <Typography variant="body2" sx={{
            mt: 0.5
          }} data-id="element-81">
                Amendments are only allowed for FT/IBFT transactions that have
                failed at the TITLE_FETCH stage.
              </Typography>
            </Alert> : <>
              <Stepper activeStep={activeStep} sx={{
            mb: 4,
            mt: 2
          }} data-id="element-82">
                {steps.map(label => <Step key={label} data-id="element-83">
                    <StepLabel data-id="element-84">{label}</StepLabel>
                  </Step>)}
              </Stepper>

              {error && <Alert severity="error" sx={{
            mb: 3
          }} onClose={() => setError(null)} data-id="element-85">
                  {error}
                </Alert>}

              {/* Step 0: Enter New Account */}
              {activeStep === 0 && <Box data-id="element-86">
                  <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-87">
                    <Typography variant="caption" color="text.secondary" data-id="element-88">
                      Current Account / IBAN
                    </Typography>
                    <Typography variant="body1" fontFamily="monospace" fontWeight={600} data-id="element-89">
                      {transaction.beneficiaryIBAN || 'N/A'}
                    </Typography>
                  </Paper>

                  <Typography variant="subtitle2" gutterBottom data-id="element-90">
                    Enter New Account Number
                  </Typography>
                  <TextField placeholder="Enter new IBAN or Account Number" fullWidth value={newAccount} onChange={e => {
              setNewAccount(e.target.value);
              setError(null);
            }} disabled={isLoading} sx={{
              mb: 2
            }} data-id="element-91" />
                  <Button variant="contained" fullWidth onClick={handleFetchTitle} disabled={isLoading || !newAccount.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-93" /> : <Icon icon="lucide:search" width={16} data-id="element-94" />} sx={{
              bgcolor: '#0EA5E9',
              '&:hover': {
                bgcolor: '#0284C7'
              }
            }} data-id="element-92">
                    Fetch Account Title
                  </Button>
                </Box>}

              {/* Step 1: Verify Title */}
              {activeStep === 1 && fetchedTitle && <Box data-id="element-95">
                  <Alert severity="info" sx={{
              mb: 3
            }} data-id="element-96">
                    Please verify if the fetched title matches the intended
                    beneficiary.
                  </Alert>

                  <Paper variant="outlined" sx={{
              p: 3,
              mb: 3,
              textAlign: 'center',
              borderColor: '#0EA5E9',
              bgcolor: 'rgba(14, 165, 233, 0.05)'
            }} data-id="element-97">
                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom data-id="element-98">
                      Fetched Account Title
                    </Typography>
                    <Typography variant="h6" color="primary.main" fontWeight={700} data-id="element-99">
                      {fetchedTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{
                mt: 1,
                fontFamily: 'monospace'
              }} data-id="element-100">
                      Account: {newAccount}
                    </Typography>
                  </Paper>

                  <Box sx={{
              display: 'flex',
              gap: 2
            }} data-id="element-101">
                    <Button variant="outlined" color="error" fullWidth onClick={handleRejectTitle} startIcon={<Icon icon="lucide:x" width={16} data-id="element-103" />} data-id="element-102">
                      Reject Title
                    </Button>
                    <Button variant="contained" color="success" fullWidth onClick={handleAcceptTitle} startIcon={<Icon icon="lucide:check" width={16} data-id="element-105" />} data-id="element-104">
                      Accept Title
                    </Button>
                  </Box>
                </Box>}

              {/* Step 2: Submit Reason */}
              {activeStep === 2 && <Box data-id="element-106">
                  <Alert severity="success" sx={{
              mb: 3
            }} data-id="element-107">
                    Title verified. Please provide a reason for this amendment.
                    This will be sent to a checker for approval.
                  </Alert>

                  <Paper variant="outlined" sx={{
              p: 2,
              mb: 3,
              bgcolor: '#F8FAFC'
            }} data-id="element-108">
                    <Grid container spacing={2} data-id="element-109">
                      <Grid item xs={6} data-id="element-110">
                        <Typography variant="caption" color="text.secondary" data-id="element-111">
                          New Account
                        </Typography>
                        <Typography variant="body2" fontFamily="monospace" fontWeight={600} data-id="element-112">
                          {newAccount}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} data-id="element-113">
                        <Typography variant="caption" color="text.secondary" data-id="element-114">
                          Verified Title
                        </Typography>
                        <Typography variant="body2" fontWeight={600} data-id="element-115">
                          {fetchedTitle}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>

                  <TextField label="Amendment Reason" placeholder="Enter reason for amendment..." multiline rows={4} fullWidth required value={reason} onChange={e => {
              setReason(e.target.value);
              setError(null);
            }} disabled={isLoading} data-id="element-116" />
                </Box>}
            </>}
        </Box>
      </DialogContent>

      <DialogActions sx={{
      px: 3,
      pb: 2
    }} data-id="element-117">
        <Button onClick={handleClose} disabled={isLoading} data-id="element-118">
          Cancel
        </Button>
        {isEligible && activeStep === 2 && <Button variant="contained" onClick={handleConfirm} disabled={isLoading || !reason.trim()} startIcon={isLoading ? <CircularProgress size={16} color="inherit" data-id="element-120" /> : <Icon icon="lucide:send" width={16} data-id="element-121" />} sx={{
        bgcolor: '#0EA5E9',
        '&:hover': {
          bgcolor: '#0284C7'
        }
      }} data-id="element-119">
            Submit for Approval
          </Button>}
      </DialogActions>
    </Dialog>;
};
export default AmendTransactionDialog;